import { useEffect, useState } from 'react'
import projectsApi from 'screens/Projects/projects.api'
import { IProject, ISlotTeamMemberRole, ProjectSlotRole, ProjectToSlot } from 'screens/Projects/projects.types'
import { IFullTeam } from 'screens/Teams/teams.types'
import eventBus from 'services/eventBus'
import Button from 'shared/Buttons/Button/Button'
import TextField from 'shared/Forms/TextField'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import Dropdown from 'shared/Inputs/Dropdown'
import Modal from 'shared/Modal'
import ModalHeader from 'shared/Modal/ModalHeader'

interface IProps {
  onSelect: (slotTeamMemberRoles: ISlotTeamMemberRole[]) => void
  onClose: () => void
  team: IFullTeam
  slotTeamMemberRoles: ISlotTeamMemberRole[]
  projectId: IProject['id']
  slot: ProjectToSlot
}

const SelectRolesModal = (props: IProps) => {
  const [memberRoles, setMemberRoles] = useState<Record<string, ProjectSlotRole>>({})
  const { wait, loading } = useHttpLoader()

  const slotRoles = props.slot.roles

  const ownerInMembers = props.team.members.some((m) => m.userId === props.team.owner.id)

  const fullTeamMembers = ownerInMembers
    ? [props.team.owner, ...props.team.members.filter((m) => m.userId !== props.team.owner.id).map((m) => m.user)]
    : props.team.members.map((m) => m.user)

  /* ------------------------- Изменение ролей пользователя ----------------------- */

  const changeUserRoles = (userId: number) => (role: ProjectSlotRole | null) => {
    setMemberRoles({ ...memberRoles, [userId]: role })
  }

  const handleSave = () => {
    const dto = Object.entries(memberRoles).map(([userId, role]) => ({ userId: +userId, roleId: role.id }))
    wait(
      projectsApi.setProjectSlotTeamMemberRoles(props.projectId, props.slot.id, dto),
      (resp) => {
        if (resp.status === 'success') {
          props.onSelect(resp.body)
          props.onClose()
        }
      },
      () => {
        eventBus.pub('ntf.message', { type: 'error', message: 'Произошла ошибка при обновлении ролей' })
      }
    )
  }

  useEffect(() => {
    if (props.slotTeamMemberRoles.length) {
      const defaultUserRoles = fullTeamMembers.reduce((dict, member) => {
        const settedMemberRole = props.slotTeamMemberRoles.find((mr) => mr.userId === member.id)
        const slotRole = props.slot.roles.find((sr) => sr.roleId === settedMemberRole?.roleId)
        return {
          ...dict,
          [member.id]: slotRole || null,
        }
      }, {})
      setMemberRoles(defaultUserRoles)
    }
  }, [props.team, props.slotTeamMemberRoles])

  return (
    <Modal onClose={props.onClose} size='sm' shouldAnimate={false}>
      <ModalHeader onClose={props.onClose}>Указание ролей</ModalHeader>
      <div className='modal-content flex-lines' style={{ height: '60vh' }}>
        {fullTeamMembers.map((user) => (
          <div key={user.id} className='flex-lines'>
            <TextField header={user.info.name}>
              <Dropdown
                loading={loading}
                options={slotRoles}
                selectedOption={memberRoles[user.id]}
                title='Выберите роль'
                onSelect={changeUserRoles(user.id)}
                getOptionKey={(o) => o?.id}
                getOptionLabel={(o) => o?.role?.title ?? ''}
              />
            </TextField>
          </div>
        ))}
      </div>
      <div className='modal-footer'>
        <Button theme='grey-outline' style={{ width: 'fit-content' }} onClick={props.onClose}>
          Отмена
        </Button>
        <Button theme='primary' style={{ width: 'fit-content' }} loading={loading} onClick={handleSave}>
          Сохранить
        </Button>
      </div>
    </Modal>
  )
}

export default SelectRolesModal
